import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { t, useLocalization } from '../../../../../../shared/components/l10n/l10n';
import { InvoicingMessageType, InvoicingMessage } from '../../book-and-send/types';
import internalApiClient from '../../../../../../shared/util/api/internalApiClient';
import MoneyflowInvalidOfferDialogComponent from './MoneyflowInvalidOfferDialog.Component';
import { RouteDisclosure } from '../../../../../../shared/components/RouteDisclosure';
import MoneyflowLoadingDialog from './MoneyflowLoadingDialog';
import { MoneyflowOfferStatusResult, MoneyflowOfferStatusEnum } from '../../../../../../../types/internal-api';

enum OfferFetchState {
    Initial,
    Refetch,
    Finished,
}

export default function MoneyflowDialog() {
    const { state, pathname } = useLocation();
    const navigate = useNavigate();
    const loadingTexts = useLocalization(textIds);
    const [loadingState, setLoadingState] = useState<OfferFetchState>(OfferFetchState.Initial);
    const [offerStatus, setOfferStatus] = useState<{ isValid: boolean; messages: InvoicingMessage[]; hasError?: boolean }>({
        isValid: false,
        messages: [],
    });

    const disclosure = <RouteDisclosure routeTrigger={'#moneyflow'} />;
    const loadingOffer = loadingState !== OfferFetchState.Finished;
    const { draftId } = state || ({} as any);
    const loading = loadingTexts || loadingOffer;

    const hide = () => navigate(pathname);
    const showBookAndSendDialog = () => navigate(pathname + '#book-draft', { state });
    const updateOfferApiCall = async () =>
        await internalApiClient.post<MoneyflowOfferStatusResult>('moneyflow/offer/update', { invoiceDraftId: draftId });
    const getOfferApiCall = async () =>
        await internalApiClient.get<MoneyflowOfferStatusResult>(`moneyflow/offer/status/${draftId}`);
    const getOffer = async ({
        apiAction,
        nextStateOnInvalidOffer = OfferFetchState.Refetch,
    }: {
        apiAction: () => Promise<any>;
        nextStateOnInvalidOffer?: OfferFetchState;
    }) => {
        try {
            const response = await apiAction();
            const data = response?.data || { offerStatus: MoneyflowOfferStatusEnum.Declined };
            const { offerStatus } = data;
            const isOfferValid = offerStatus === MoneyflowOfferStatusEnum.Valid;
            const nextState = isOfferValid ? OfferFetchState.Finished : nextStateOnInvalidOffer;

            if (isOfferValid) {
                setOfferStatus({ isValid: isOfferValid, messages: [{ type: InvoicingMessageType.Info, message: data.info }] });
            } else {
                setOfferStatus({
                    isValid: isOfferValid,
                    messages: [
                        { type: InvoicingMessageType.Info, message: data.info },
                        { type: InvoicingMessageType.Info, message: t`FORM_BOOKANDSEND_ERRORS_MONEYFLOW_REJECTED` },
                    ],
                });
            }

            setLoadingState(nextState);
        } catch (error) {
            setOfferStatus({
                isValid: false,
                hasError: true,
                messages: [{ type: InvoicingMessageType.Error, message: t`FORM_BOOKANDSEND_ERRORS_UNKNOWN_ERROR` }],
            });
            setLoadingState(OfferFetchState.Finished);
        }
    };

    useEffect(() => {
        switch (loadingState) {
            case OfferFetchState.Initial:
                getOffer({ apiAction: updateOfferApiCall });
                break;
            case OfferFetchState.Refetch:
                getOffer({ apiAction: getOfferApiCall, nextStateOnInvalidOffer: OfferFetchState.Finished });
                break;
        }
    }, [loadingState]);

    useEffect(() => {
        if (loadingOffer) return;

        if (offerStatus.isValid) {
            showBookAndSendDialog();
        }
    }, [loadingOffer]);

    if (loading) {
        return <MoneyflowLoadingDialog disclosure={disclosure} hide={hide} />;
    } else {
        return <MoneyflowInvalidOfferDialogComponent disclosure={disclosure} hide={hide} {...offerStatus} />;
    }
}

const textIds = [
    'FORM_BOOKANDSEND_ERRORS_MONEYFLOW_REJECTED',
    'FORM_BOOKANDSEND_ERRORS_UNKNOWN_ERROR',
    'FORM_BOOKANDSEND_MONEYFLOW_HEADER_NOTQUALIFIED',
    'FORM_BOOKANDSEND_MONEYFLOW_LOADING_HEADER',
    'SHARED_CONFIRM_CLOSEWINDOW',
    'SHARED_FORM_OK',
    'SHARED_FORM_TOOLTIP_BADGE_ERROR',
    'SHARED_FORM_TOOLTIP_BADGE_INFORMATION',
    'SHARED_FORM_TOOLTIP_BADGE_WARNING',
];
